import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSession } from 'src/contexts/SessionContext';
import { bulkInvite, createClient } from 'src/models/client/client';
import { fetchAllLifecycleStages } from 'src/models/lifecycle/lifecycle';
import { fetchLocations } from 'src/models/location/location';
import { fetchTrainers } from 'src/models/user/user';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';

export const defaultFields = [
  { label: 'Email', type: 'email' },
  { label: 'First Name', type: 'first_name' },
  { label: 'Last Name', type: 'last_name' },
  { label: 'Street', type: 'street_address' },
  { label: 'City', type: 'city' },
  { label: 'State', type: 'state' },
  { label: 'Zip Code', type: 'zip_code' },
];

export const defaultProfileFields = [
  { label: 'Phone Number', type: 'phone_number' },
  { label: 'Gender', type: 'gender' },
  { label: 'Height', type: 'height' },
  { label: 'System of Measurement', type: 'uses_metric' },
  { label: 'Birthdate', type: 'birthdate' },
  { label: 'Weight', type: 'weight' },
  { label: 'Location', type: 'location_id' },
];

export const useNewClientPage = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'client.newClientPage',
  });
  const { t: tGlobal } = useTranslation('translation');

  const { session, sessionTrainer, refreshSessionAndUser } = useSession();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: lifecycleStages = [] } = useQuery({
    queryKey: ['lifecycleStages'],
    queryFn: fetchAllLifecycleStages,
  });

  const { data: trainers = [] } = useQuery({
    queryKey: ['trainers'],
    queryFn: () => fetchTrainers(),
  });

  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: () =>
      fetchLocations({
        fetchAll: true,
        sort: 'name',
        sortDir: 'asc',
      }),
    staleTime: 60000,
    select: (data) => data.locations,
  });

  const createClientMutation = useMutation({
    mutationFn: async ({
      invites,
      data,
      bulk = false,
    }: {
      invites?: any;
      data?: any;
      bulk?: boolean;
    }) => {
      if (bulk) {
        return await bulkInvite({ client: data });
      }
      const promises = invites.map((v) =>
        createClient({ client: { ...data, ...v } })
      );
      return Promise.all(promises);
    },
    onSuccess: () => {
      toast.success(
        t('saveClientSuccess', {
          client: tGlobal('trainer.client.one').toLowerCase(),
        })
      );
      refreshSessionAndUser();
      navigate('/clients');
    },
    onError: (errors: any) => {
      if (
        errors &&
        errors.message &&
        errors.message.includes('returned the new id')
      ) {
        toast.success(
          t('saveClientSuccess', {
            client: tGlobal('trainer.client.one').toLowerCase(),
          })
        );
        refreshSessionAndUser();
        navigate('/clients');
        queryClient.invalidateQueries({ queryKey: ['clients'] });
      } else {
        toast.error(api.formatError(errors));
      }
    },
  });

  const getProfileFieldOptions = (profileFields) => {
    const options: any[] = [];
    const profileValues = profileFields.map((f) => f.id);
    defaultProfileFields.forEach((f) => {
      if (!profileValues.includes(f.type)) {
        options.push({ label: f.label, value: f.type });
      }
    });
    sessionTrainer?.trainerProfileFields?.forEach((value) => {
      if (!profileValues.includes(value)) {
        options.push({ label: value, value });
      }
    });
    return options;
  };

  const getInitialProfileFields = (phoneNumber: string | null) => {
    const fields = session?.newClientFields || [];
    const requiredFields = session?.newClientRequiredFields || [];

    // Combine and remove duplicates by id
    const allFields = [...fields, ...requiredFields];
    const uniqueFields = allFields.reduce((acc, current) => {
      if (!acc.find((field) => field.id === current.id)) {
        acc.push(current);
      }
      return acc;
    }, fields.slice(0, 0));

    const defaultFieldTypes = defaultFields.map((f) => f.type);
    const profileFields = uniqueFields.filter(
      (f) => !defaultFieldTypes.includes(f.id)
    );

    const result = profileFields.map((field) => {
      let value: any = null;
      if (field.id === 'time_zone') {
        value = sessionTrainer?.timeZone || null;
      }

      return {
        name: field.name,
        id: field.id,
        value: value,
      };
    });
    if (phoneNumber) {
      const phoneField = result.find((f) => f.id === 'phone_number');
      if (phoneField) {
        phoneField.value = phoneNumber;
      } else {
        result.push({
          name: 'Phone Number',
          id: 'phone_number',
          value: phoneNumber,
        });
      }
    }
    return result;
  };

  return {
    lifecycleStages,
    createClientMutation,
    getProfileFieldOptions,
    getInitialProfileFields,
    trainers,
    locations,
  };
};
